import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';

import {
  deleteRevokeInframailIpsRequest,
  getInframailIpsDomainsRequest,
  getInframailIpsRequest,
} from './extra-actions';

import {
  InframailData,
  InframailIpsDomain,
  InframailPaginationMeta,
} from './types';
import { flattenOrdersForTable } from './helpers/helper';

interface RequestState {
  status: RequestStatus;
  message: string;
  error: any;
}

interface State {
  getInframailIpsRequest: RequestState;
  deleteRevokeInframailIpsRequest: RequestState;
  getInframailIpsDomainsRequest: RequestState;

  inframailIps: InframailData[];
  inframailIpsMeta: InframailPaginationMeta;
  inframailIpsDomains: Record<string, InframailIpsDomain[]>;
  inframailIpsDomainsLoading: Record<string, boolean>;
}

const initialState: State = {
  getInframailIpsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getInframailIpsDomainsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteRevokeInframailIpsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  inframailIps: [],
  inframailIpsMeta: {
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  },
  inframailIpsDomains: null,
  inframailIpsDomainsLoading: null,
};

const inframailIpsSlice = createSlice({
  name: 'inframailIps',
  initialState,
  reducers: {
    resetGetInframailIpsRequestState: (state) => {
      state.getInframailIpsRequest = initialState.getInframailIpsRequest;
    },
    resetGetInframailIpsDomainsRequestState: (state) => {
      state.getInframailIpsDomainsRequest =
        initialState.getInframailIpsDomainsRequest;
    },
    resetDeleteRevokeInframailIpsRequestState: (state) => {
      state.deleteRevokeInframailIpsRequest =
        initialState.deleteRevokeInframailIpsRequest;
    },
    updateInframailIpsMeta: (state, action) => {
      state.inframailIpsMeta = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Inframail Ips
    builder.addCase(getInframailIpsRequest.pending, (state) => {
      state.getInframailIpsRequest.status = RequestStatus.Pending;
      state.getInframailIpsRequest.error = null;
      state.getInframailIpsRequest.message = null;
    });
    builder.addCase(getInframailIpsRequest.fulfilled, (state, action) => {
      state.getInframailIpsRequest.status = RequestStatus.Succeeded;
      state.inframailIps = flattenOrdersForTable(
        action.payload.payload.orders ?? [],
      );
      state.inframailIpsMeta = action.payload.payload.meta;
    });
    builder.addCase(getInframailIpsRequest.rejected, (state, action) => {
      state.getInframailIpsRequest.status = RequestStatus.Failed;
      state.getInframailIpsRequest.message = action.payload.message;
      state.getInframailIpsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Inframail Ips Domains
    builder.addCase(getInframailIpsDomainsRequest.pending, (state, action) => {
      state.getInframailIpsDomainsRequest.status = RequestStatus.Pending;
      state.getInframailIpsDomainsRequest.error = null;
      state.getInframailIpsDomainsRequest.message = null;
      state.inframailIpsDomainsLoading = {
        ...(state.inframailIpsDomainsLoading ?? {}),
        [action.meta.arg]: true,
      };
    });
    builder.addCase(
      getInframailIpsDomainsRequest.fulfilled,
      (state, action) => {
        state.getInframailIpsDomainsRequest.status = RequestStatus.Succeeded;
        state.inframailIpsDomains = {
          ...(state.inframailIpsDomains ?? {}),
          [action.meta.arg]: action.payload.payload.domains,
        };
        state.inframailIpsDomainsLoading = {
          ...(state.inframailIpsDomainsLoading ?? {}),
          [action.meta.arg]: false,
        };
      },
    );
    builder.addCase(getInframailIpsDomainsRequest.rejected, (state, action) => {
      state.getInframailIpsDomainsRequest.status = RequestStatus.Failed;
      state.getInframailIpsDomainsRequest.message = action.payload.message;
      state.getInframailIpsDomainsRequest.error =
        !action.payload.isHandled && action.payload;
      state.inframailIpsDomainsLoading = {
        ...(state.inframailIpsDomainsLoading ?? {}),
        [action.meta.arg]: false,
      };
    });

    // Delete or Revoke Inframail Ips
    builder.addCase(deleteRevokeInframailIpsRequest.pending, (state) => {
      state.deleteRevokeInframailIpsRequest.status = RequestStatus.Pending;
      state.deleteRevokeInframailIpsRequest.error = null;
      state.deleteRevokeInframailIpsRequest.message = null;
    });
    builder.addCase(
      deleteRevokeInframailIpsRequest.fulfilled,
      (state, action) => {
        state.deleteRevokeInframailIpsRequest.status = RequestStatus.Succeeded;
        state.deleteRevokeInframailIpsRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      deleteRevokeInframailIpsRequest.rejected,
      (state, action) => {
        state.deleteRevokeInframailIpsRequest.status = RequestStatus.Failed;
        state.deleteRevokeInframailIpsRequest.message = action.payload.message;
        state.deleteRevokeInframailIpsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
  },
});

export const {
  resetGetInframailIpsRequestState,
  resetGetInframailIpsDomainsRequestState,
  resetDeleteRevokeInframailIpsRequestState,
  updateInframailIpsMeta,
} = inframailIpsSlice.actions;

export default inframailIpsSlice.reducer;
